const CSV_FORMULA_INJECTION_PREFIX = /^[=+\-@\t\r|]/;
const CSV_FORMULA_INJECTION_PREFIX_WITH_BACKSLASH = /^[=+\-@\t\r|\\]/;

const UNICODE_DIRECTIONAL_PATTERN =
  /[\u200B-\u200F\u202A-\u202E\u2060-\u2069\uFEFF]/g;
const HTML_ANGLE_BRACKET_PATTERN = /</g;
const FILENAME_UNSAFE_CHAR_PATTERN = /[<>:"/\\|?*\p{Cc}]/gu;
const UNICODE_LINE_SEPARATOR = '\u2028';
const UNICODE_PARAGRAPH_SEPARATOR = '\u2029';

function stripDisallowedControlCharacters(value: string): string {
  return value
    .replace(/\p{Cc}/gu, (char) =>
      char === '\t' || char === '\n' || char === '\r' ? char : '',
    )
    .replaceAll(UNICODE_LINE_SEPARATOR, '')
    .replaceAll(UNICODE_PARAGRAPH_SEPARATOR, '');
}

export function sanitizeCoPilotExportText(value: string): string {
  return stripDisallowedControlCharacters(value).replace(
    UNICODE_DIRECTIONAL_PATTERN,
    '',
  );
}

export function neutralizeCoPilotExportHtmlInjection(value: string): string {
  return sanitizeCoPilotExportText(value).replace(
    HTML_ANGLE_BRACKET_PATTERN,
    '&lt;',
  );
}

export function sanitizeCoPilotExportMarkdownPlainText(value: string): string {
  return neutralizeCoPilotExportHtmlInjection(value);
}

export function sanitizeCoPilotExportMarkdownCodeContent(
  value: string,
): string {
  return sanitizeCoPilotExportText(value);
}

function escapeCsvCell(value: string): string {
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function prefixCsvFormulaInjection(value: string): string {
  if (
    CSV_FORMULA_INJECTION_PREFIX.test(value) ||
    CSV_FORMULA_INJECTION_PREFIX_WITH_BACKSLASH.test(value)
  ) {
    return `'${value}`;
  }
  return value;
}

export function sanitizeCoPilotExportCsvCell(value: string): string {
  const sanitized = sanitizeCoPilotExportText(String(value));
  return escapeCsvCell(prefixCsvFormulaInjection(sanitized));
}

export function sanitizeCoPilotExportFilenameSegment(value: string): string {
  return sanitizeCoPilotExportText(value)
    .replace(FILENAME_UNSAFE_CHAR_PATTERN, '')
    .replace(/\.\.+/g, '.')
    .trim();
}

export function sanitizeCoPilotExportRequestSlug(value: string): string {
  const sanitized = sanitizeCoPilotExportFilenameSegment(value)
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_-]/g, '')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');

  return sanitized || 'session_export';
}

export function sanitizeCoPilotExportAttachmentFilename(
  filename: string,
): string {
  const sanitized = sanitizeCoPilotExportFilenameSegment(filename.trim());
  return sanitized || 'session_export';
}
